import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LiveEditing from './Components/LiveEditing';
import { gtmMiddleware } from './Middlewares/Gtm.middleware';
import MiniCart from './Components/MiniCart';
import MiniCartHeaderIcon from './Components/MiniCartHeaderIcon';
import MiniCartContainer from './Containers/MiniCart.container';
import MiniCartHeaderIconContainer from './Containers/MiniCartHeaderIcon.container';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearchContainer from './Components/FacetedSearchContainer';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import { ExpandableCategoryDescription } from './Vanilla/CategoryDescription';
import { CategoryNavigationScrollHint } from './Vanilla/CategoryNavigation';
import DynamicComponent from './Components/DynamicComponent';
import ProductConfiguratorContainer from './Containers/ProductConfigurator.container';
import AddonProductContainer from './Containers/AddonProduct.container';
import '../Scripts/Vanilla/GtmEvents';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
  app,
  preloadState,
  composeWithDevTools(applyMiddleware(thunk, historyMiddleware, gtmMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers[key]) {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCartContainer />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('miniCartHeaderIcon')) {
        renderReact(
            <Provider store={store}>
                <MiniCartHeaderIconContainer />
            </Provider>,
            document.getElementById('miniCartHeaderIcon')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }

  if (document.getElementById('ProductConfigurator')) {
    const configurator = document.getElementById('ProductConfigurator');

    const {
      packagePriceNew,
      packagePriceOld,
      packagePriceNormal,
      isPackageProduct,
      configVariant,
      baseProduct,
      cssClass,
      label,
      labelEmpty,
    } = configurator.dataset;

    renderReact(
      <Provider store={store}>
        <ProductConfiguratorContainer
          {...{
            packagePriceNew,
            packagePriceOld,
            packagePriceNormal,
            isPackageProduct,
            configVariant,
            baseProduct,
            cssClass,
            label,
            labelEmpty,
          }}
        />
      </Provider>,
      configurator
    );
  }

  if (document.getElementById('AddonProduct')) {
    const configurator = document.getElementById('AddonProduct');

    const {
      baseProduct,
      cssClass,
      label,
      canBuy,
      formattedPrice,
      formattedCampaignPrice,
      product,
      variantPrice,
      listPrice,
      statusCode,
      statusText,
    } = configurator.dataset;

    renderReact(
      <Provider store={store}>
        <AddonProductContainer
          {...{
            baseProduct,
            cssClass,
            label,
            canBuy,
            formattedPrice,
            formattedCampaignPrice,
            product,
            variantPrice,
            listPrice,
            statusCode,
            statusText,
          }}
        />
      </Provider>,
      configurator
    );
  }

  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchContainer />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(rootElement.querySelectorAll('template')).map(
        (img) => ({
          html: img.innerHTML,
          src: img.dataset.src,
        })
      );
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Containers/BuyButton.container'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={store}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }

  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <OrderList />
      </Provider>,
      document.getElementById('orderHistoryPage')
    );
  }

  if (document.querySelectorAll('#language-picker-container')) {
    const LanguagePicker = DynamicComponent({
      loader: () => import('./Components/LanguagePicker'),
    });
    Array.from(document.querySelectorAll('#language-picker-container')).forEach(
      (picker) => {
        try {
          renderReact(
            <Provider store={store}>
              <LanguagePicker
                items={JSON.parse(picker.querySelector('json-data').innerText)}
              />
            </Provider>,
            picker
          );
        } catch (error) {
          console.error('Error parsing JSON data:', error);
        }
      }
    );
  }

  if (document.querySelectorAll('#lipscore-service-reviews').length > 0) {
    const HeaderServiceRating = DynamicComponent({
      loader: () => import('./Components/HeaderServiceRating'),
    });
    Array.from(document.querySelectorAll('#lipscore-service-reviews')).forEach(
      (container) => {
        renderReact(
          <Provider store={store}>
            <HeaderServiceRating />
          </Provider>,
          container
        );
      }
    );
  }

  // re-run on every bootstrap so the toggle survives AJAX filter swaps of #search-result
  if (document.querySelector('.category-page__intro-description')) {
    ExpandableCategoryDescription();
  }

  if (document.querySelector('.category-page__navigation')) {
    CategoryNavigationScrollHint();
  }
};

bootstrapComponents();

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('miniCart')) {
    document
      .getElementsByTagName('html')[0]
      .addEventListener('click', function (evt) {
        var cart = document.querySelector('.mini-cart-holder');
        if (
          cart.classList.contains('active') &&
          evt.target.classList.contains('mini-cart-background')
        ) {
          document.querySelectorAll('.mini-cart-toggle').forEach(function (e) {
            e.parentNode.childNodes[0].click();
          });
        }
      });
  }
});
